import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../services/api';
import { changePassword } from '../../services/authService';
import styles from './AccountSettings.module.css';

const PASSWORD_REQUIREMENTS = 'Min 8 characters with at least one letter and one number';

export default function AccountPasswordChange() {
    const { user, updateUserData } = useAuth();
    const navigate = useNavigate();

    const [accountMeta, setAccountMeta] = useState({
        has_password: undefined,
        linked_providers: [],
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordSaved, setPasswordSaved] = useState(false);
    const [lastPasswordAction, setLastPasswordAction] = useState(null);

    useEffect(() => {
        if (user) {
            setAccountMeta({
                has_password: user.has_password,
                linked_providers: user.linked_providers || [],
            });
        }
    }, [user]);

    useEffect(() => {
        let isMounted = true;

        const loadAccountMeta = async () => {
            try {
                const account = await apiRequest('/account/me');
                if (!isMounted) return;

                setAccountMeta({
                    has_password: account.has_password,
                    linked_providers: account.linked_providers || [],
                });
            } catch (error) {
                console.error('Error loading account details:', error);
            }
        };

        if (user) loadAccountMeta();

        return () => {
            isMounted = false;
        };
    }, [user]);

    const validatePasswordForm = () => {
        const newErrors = {};

        if (hasExistingPassword && !passwordData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!/^.{8,}$/.test(passwordData.newPassword) || !/[a-zA-Z]/.test(passwordData.newPassword) || !/\d/.test(passwordData.newPassword)) {
            newErrors.newPassword = PASSWORD_REQUIREMENTS;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setPasswordErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePasswordInputChange = (field, value) => {
        setPasswordData(prev => ({ ...prev, [field]: value }));
        setPasswordErrors(prev => ({ ...prev, [field]: undefined }));
        setPasswordSaved(false);
        setLastPasswordAction(null);
    };

    const handlePasswordSave = async () => {
        if (isPasswordMetaLoading || !validatePasswordForm()) return;

        setPasswordSaving(true);
        setPasswordSaved(false);

        try {
            await changePassword({
                currentPassword: hasExistingPassword ? passwordData.currentPassword : undefined,
                newPassword: passwordData.newPassword,
            });

            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            setLastPasswordAction(hasExistingPassword ? 'updated' : 'added');
            setAccountMeta(prev => ({ ...prev, has_password: true }));
            await updateUserData({ has_password: true });
            setPasswordSaved(true);

            setTimeout(() => {
                setPasswordSaved(false);
                setLastPasswordAction(null);
            }, 5000);
        } catch (error) {
            const message = error?.message || 'Something went wrong. Please try again.';

            if (message.toLowerCase().includes('current') || message.toLowerCase().includes('incorrect')) {
                setPasswordErrors(prev => ({ ...prev, currentPassword: 'Current password is incorrect' }));
            } else {
                setPasswordErrors(prev => ({ ...prev, form: message }));
            }
        } finally {
            setPasswordSaving(false);
        }
    };

    const hasPasswordChanged = () => (
        Boolean(passwordData.currentPassword) ||
        Boolean(passwordData.newPassword) ||
        Boolean(passwordData.confirmPassword)
    );

    const formatProviders = (providers = []) => {
        if (!providers.length) return 'your social provider';
        return providers.map(provider => provider.charAt(0).toUpperCase() + provider.slice(1)).join(' or ');
    };

    const isPasswordMetaLoading = accountMeta.has_password === undefined;
    const hasExistingPassword = accountMeta.has_password === true;
    const isAddingPassword = accountMeta.has_password === false;

    return (
        <main className={styles.page}>
            <div className={styles.inner}>
                <div className={styles.screenHeader}>
                    <button
                        className={styles.backBtn}
                        onClick={() => navigate('/settings')}
                        title="Back to settings"
                    >
                        ← Back
                    </button>
                    <h1 className={styles.screenTitle}>Change Password</h1>
                    <div className={styles.headerSpacer} />
                </div>

                <div className={styles.subtitle}>
                    Keep your account secure by updating your password regularly
                </div>

                <div className={styles.formContainer}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>{isAddingPassword ? 'Add an Aluvo password' : 'Password'}</h2>
                        <p className={styles.sectionDescription}>
                            {isAddingPassword
                                ? 'Create an Aluvo password so you can sign in with email and password in addition to your linked social account.'
                                : 'Enter your current password to confirm your identity, then set a new one.'}
                        </p>
                    </div>

                    {isPasswordMetaLoading ? (
                        <div className={styles.socialLoginNotice}>
                            <div className={styles.noticeIcon}>…</div>
                            <div>
                                <h3>Checking sign-in method</h3>
                                <p>We’re confirming whether this account has an Aluvo password or uses a linked social provider.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isAddingPassword && (
                                <div className={styles.socialLoginNotice}>
                                    <div className={styles.noticeIcon}>🔐</div>
                                    <div>
                                        <h3>Social login connected</h3>
                                        <p>
                                            You currently sign in with {formatProviders(accountMeta.linked_providers)}. Add an Aluvo password below if you also want to sign in with email and password.
                                        </p>
                                    </div>
                                </div>
                            )}
                            {passwordSaved && (
                                <div className={styles.inlineSuccess}>
                                    {lastPasswordAction === 'added' ? '✓ Password added successfully.' : '✓ Password updated successfully.'}
                                </div>
                            )}
                            {passwordErrors.form && (
                                <div className={styles.inlineError}>{passwordErrors.form}</div>
                            )}

                            {hasExistingPassword && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label} htmlFor="currentPassword">Current Password</label>
                                    <input
                                        id="currentPassword"
                                        type="password"
                                        className={`${styles.input} ${passwordErrors.currentPassword ? styles.inputError : ''}`}
                                        value={passwordData.currentPassword}
                                        onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                                        placeholder="Enter current password"
                                        autoComplete="current-password"
                                    />
                                    {passwordErrors.currentPassword && (
                                        <p className={styles.errorText}>{passwordErrors.currentPassword}</p>
                                    )}
                                </div>
                            )}

                            <div className={styles.fieldGroup}>
                                <label className={styles.label} htmlFor="newPassword">New Password</label>
                                <input
                                    id="newPassword"
                                    type="password"
                                    className={`${styles.input} ${passwordErrors.newPassword ? styles.inputError : ''}`}
                                    value={passwordData.newPassword}
                                    onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                                    placeholder="8+ chars, letters + numbers"
                                    autoComplete="new-password"
                                />
                                {passwordErrors.newPassword ? (
                                    <p className={styles.errorText}>{passwordErrors.newPassword}</p>
                                ) : (
                                    <p className={styles.hint}>{PASSWORD_REQUIREMENTS}</p>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label} htmlFor="confirmPassword">Confirm New Password</label>
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    className={`${styles.input} ${passwordErrors.confirmPassword ? styles.inputError : ''}`}
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                                    placeholder="Repeat new password"
                                    autoComplete="new-password"
                                />
                                {passwordErrors.confirmPassword && (
                                    <p className={styles.errorText}>{passwordErrors.confirmPassword}</p>
                                )}
                            </div>

                            <button
                                className={`${styles.saveBtn} ${
                                    passwordSaved ? styles.saveBtnSuccess : ''
                                } ${(!hasPasswordChanged() || passwordSaving) ? styles.saveBtnDisabled : ''}`}
                                onClick={handlePasswordSave}
                                disabled={!hasPasswordChanged() || passwordSaving}
                            >
                                {passwordSaving
                                    ? (isAddingPassword ? 'Adding...' : 'Updating...')
                                    : passwordSaved
                                        ? (lastPasswordAction === 'added' ? '✓ Added' : '✓ Updated')
                                        : (isAddingPassword ? 'Add Password' : 'Update Password')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
